import React from 'react';
import { Button } from './Button';
import { useToast } from '../../hooks/useToast';
import { useJobLock } from '../../hooks/useJobLock';
import { formatDateForInput } from '../../utils/jobDateValidation';
import type { JobDatesData, JobLockData } from '../../utils/types';
import { Calendar, Clock, Lock, Package } from 'lucide-react';

export interface SkipProductionDateFieldsProps {
  jobId: string;
  lockData?: JobLockData | null;
  hideGlassArrival: boolean;
  dates: {
    glass_arrival_date: string;
    manufacture_date: string;
    potential_delivery_date: string;
  };
  onChange: (field: keyof JobDatesData, value: string) => void;
}

export const SkipProductionDateFields: React.FC<SkipProductionDateFieldsProps> = ({
  jobId,
  lockData,
  hideGlassArrival,
  dates,
  onChange,
}) => {
  const toast = useToast();
  const { canModifyDates, showLockError } = useJobLock({
    jobId,
    currentLock: lockData || null,
  });

  const setSameAsGlassArrival = () => {
    if (!dates.glass_arrival_date) {
      toast.error('Glass Arrival Required', 'Please set Glass Arrival date first');
      return;
    }
    onChange('manufacture_date', dates.glass_arrival_date);
  };

  const handlePotentialChange = (value: string) => {
    if (!canModifyDates) {
      showLockError();
      return;
    }
    onChange('potential_delivery_date', value);
  };

  const handlePotentialClear = () => {
    if (!canModifyDates) {
      showLockError();
      return;
    }
    onChange('potential_delivery_date', '');
  };

  return (
    <div className="space-y-3">
      <h4 className="text-sm font-medium text-gray-700">Optional production dates</h4>
      <p className="text-xs text-gray-500">
        Add or adjust dates before completing production. Leave blank to keep existing values unchanged until you
        confirm.
      </p>

      {!canModifyDates && (
        <div className="bg-amber-50 border border-amber-200 rounded-md p-2 flex items-center gap-2">
          <Lock className="w-4 h-4 text-amber-600 flex-shrink-0" />
          <span className="text-xs text-amber-800">
            Potential Delivery Date is locked and cannot be modified
          </span>
        </div>
      )}

      <div className="rounded-lg p-4 space-y-6 bg-green-50 border border-green-100">
        {!hideGlassArrival && (
          <div>
            <div className="flex items-center mb-3">
              <Package className="w-4 h-4 mr-2 text-green-600" />
              <span className="text-sm font-medium uppercase tracking-wide text-gray-700">Glass Arrival</span>
            </div>
            <div className="flex gap-2 items-center">
              <input
                type="date"
                value={formatDateForInput(dates.glass_arrival_date)}
                onChange={(e) => onChange('glass_arrival_date', e.target.value)}
                className="form-input text-sm flex-1"
              />
              {dates.glass_arrival_date ? (
                <Button
                  size="sm"
                  variant="ghost"
                  type="button"
                  onClick={() => onChange('glass_arrival_date', '')}
                  className="text-xs text-red-600 hover:text-red-700 px-2"
                >
                  Clear
                </Button>
              ) : null}
            </div>
          </div>
        )}

        <div>
          <div className={`flex items-center mb-3 ${hideGlassArrival ? '' : 'justify-between'}`}>
            <div className="flex items-center">
              <Calendar className="w-4 h-4 mr-2 text-blue-600" />
              <span className="text-sm font-medium uppercase tracking-wide text-gray-700">Manufacturing</span>
            </div>
            {!hideGlassArrival && (
              <Button
                size="sm"
                variant="secondary"
                type="button"
                onClick={setSameAsGlassArrival}
                disabled={!dates.glass_arrival_date}
                className="text-xs"
              >
                Same Date
              </Button>
            )}
          </div>
          <div className="flex gap-2 items-center">
            <input
              type="date"
              value={formatDateForInput(dates.manufacture_date)}
              onChange={(e) => onChange('manufacture_date', e.target.value)}
              min={hideGlassArrival ? undefined : dates.glass_arrival_date || undefined}
              className="form-input text-sm flex-1"
            />
            {dates.manufacture_date ? (
              <Button
                size="sm"
                variant="ghost"
                type="button"
                onClick={() => onChange('manufacture_date', '')}
                className="text-xs text-red-600 hover:text-red-700 px-2"
              >
                Clear
              </Button>
            ) : null}
          </div>
        </div>

        <div>
          <div className="flex items-center mb-3">
            <Clock className="w-4 h-4 mr-2 text-purple-600" />
            <span className="text-sm font-medium uppercase tracking-wide text-gray-700">Potential Delivery</span>
          </div>
          <div className="flex gap-2 items-center">
            <input
              type="datetime-local"
              value={formatDateForInput(dates.potential_delivery_date)}
              onChange={(e) => handlePotentialChange(e.target.value)}
              min={
                hideGlassArrival
                  ? dates.manufacture_date || undefined
                  : dates.manufacture_date || dates.glass_arrival_date || undefined
              }
              disabled={!canModifyDates}
              className="form-input text-sm flex-1 disabled:bg-gray-100 disabled:cursor-not-allowed"
              style={{ minWidth: '160px' }}
            />
            {dates.potential_delivery_date ? (
              <Button
                size="sm"
                variant="ghost"
                type="button"
                onClick={handlePotentialClear}
                disabled={!canModifyDates}
                className="text-xs text-red-600 hover:text-red-700 px-2"
              >
                Clear
              </Button>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
};
